import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  findRouteRequirement,
  hasRoutePermission,
} from "../../utils/routePermissions";
import Forbidden403 from "../../pages/Errors/Forbidden403";
import Loader from "../Molecules/Loader/Loader";

interface ProtectedRouteProps {
  children: React.ReactElement;
  entity?: string; // Optional: override entity from route detection
  action?: string; // Optional: override action from route detection
  adminOnly?: boolean; // Optional: require admin access
  fallbackPath?: string; // Optional: custom redirect path for unauthorized users
}

/**
 * ProtectedRoute component that checks permissions for specific routes
 * Can be used to wrap individual route components for granular permission control
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  entity,
  action,
  adminOnly = false,
  fallbackPath = "/domain",
}) => {
  const {
    isAuthenticated,
    hasPermission,
    isAdmin,
    isPermissionsLoading,
    allowedPolicies,
  } = useAuth();
  const location = useLocation();

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the intended destination (pathname + search) before redirecting to login
    // This allows post-login redirect to the originally requested URL
    const intendedDestination = `${location.pathname}${location.search || ""}`;
    sessionStorage.setItem("intendedDestination", intendedDestination);
    return <Navigate to="/login" replace />;
  }

  // Show loader while permissions are being fetched
  if (isPermissionsLoading) {
    return <Loader isLoading={true} />;
  }

  // Check if user has no policies and redirect to profile
  const hasNoPolicies = allowedPolicies.length === 0;
  const isProfilePage = location.pathname === "/profile";
  const isDashboardPage = location.pathname === "/dashboard";

  // Redirect to profile if user has no policies and is not already on profile page or dashboard page
  // Dashboard should be accessible to all authenticated users regardless of permissions
  if (hasNoPolicies && !isProfilePage && !isDashboardPage) {
    return <Navigate to="/profile" replace />;
  }

  // If adminOnly is specified, check admin status
  if (adminOnly) {
    if (!isAdmin) {
      return <Forbidden403 />;
    }
    return children;
  }

  // Use provided entity/action or detect from route
  let requiredEntity = entity;
  let requiredAction = action;

  if (!requiredEntity || !requiredAction) {
    const routeRequirement = findRouteRequirement(location.pathname);

    if (routeRequirement) {
      requiredEntity = requiredEntity || routeRequirement.entity;
      requiredAction = requiredAction || routeRequirement.action;

      // Handle admin-only routes detected from route
      if (routeRequirement.adminOnly && !isAdmin) {
        return <Forbidden403 />;
      }
    }
  }

  // If no entity/action required, allow access (public route)
  if (!requiredEntity || !requiredAction) {
    return children;
  }

  // Check if user has the required permission
  const hasAccess = hasRoutePermission(
    location.pathname,
    hasPermission,
    isAdmin,
  );

  if (!hasAccess) {
    return <Forbidden403 />;
  }

  return children;
};

export default ProtectedRoute;
